import React, { memo, useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import action from '@flux/action';
import TotalRevenueChart from './totalRevenueChart';
import { produce, setAutoFreeze } from 'immer';
import { Tabs, Loading, ExpandButton } from '@components';
import {
  defaultTotalRevenueChartOption,
  YEARS_NEED_STACKLABEL,
} from '@components/charts/config';
import D_Currency from '@utils/currency';
// import '@style/modules/analysis/analysisRight.scss';
import '../../../../styles/modules/analysis/analysisRight.scss';
// import '@style/components/charts.scss';
import '../../../../styles/components/charts.scss';
import {
  getValueFromSearchParam,
  removeValueFromSearchParam,
  setURLSearchParam,
} from '@utils/history';
import { useHistory } from 'react-router-dom';
import searchParamKeys from '@model/searchParamKeys';
import useTheme from '@hooks/useTheme';
import { NewStackColors } from '../../../../components/charts/config';
import { titleCase } from 'title-case';
setAutoFreeze(false);
export const condition = {
  Timeline: [
    { name: 'LTM by Quarter', value: 'LTMQ' },
    { name: 'LTM', value: 'LTM' },
    { name: '1Y by Quarter', value: 'Y1Q' },
    { name: '1Y', value: 'Y1' },
    { name: '3Y', value: 'Y3' },
    // { name: '5Y', value: 'Y5' },
  ],
  Metrics: [
    { name: '--None--', value: 'default' },
    { name: 'Sales Growth', value: 'salesGrowth' },
    { name: 'Gross Margin', value: 'grossMargin' },
    { name: 'EPS', value: 'eps' },
    { name: 'Market Cap', value: 'marketCap' },
  ],
};
const SEGMENT_KEYS = ['bySegment', 'byGeography'];

// to merge segment data in diffrent case, such as 'Beauty' and 'BEAUTY'
const enhanceDataFromSameSegments = (a, b) => {
  const tickAmount = a.length;
  const res = [];

  for (let i = 0; i < tickAmount; i += 1) {
    if (a[i] !== null) {
      res[i] = a[i];
    } else {
      res[i] = b[i];
    }
  }
  return res;
};

// remove duplicate segments with different cases in out put
const mergeSegmentDataWithDifferentCase = (segmentData) => {
  const allSegments = Object.keys(segmentData);

  return allSegments.reduce((acc, e) => {
    const titleCaseSegment = titleCase(String(e).toLowerCase());
    if (
      !Array.isArray(segmentData[e]) ||
      !segmentData[e].some((d) => d !== null)
    ) {
      return acc;
    }
    if (!acc[titleCaseSegment]) {
      acc[titleCaseSegment] = segmentData[e];
    } else {
      acc[titleCaseSegment] = enhanceDataFromSameSegments(
        acc[titleCaseSegment],
        segmentData[e],
      );
    }
    return acc;
  }, {});
};

const getFirstTimelineBothHaveData = (pData, tab) => {
  const { buyer = {}, target = {} } = pData;
  if (Object.keys(buyer) === 0 || Object.keys(target) === 0) {
    return condition.Timeline[0].value;
  }
  const timelinesBothHaveData = condition.Timeline.map((t) => t.value).filter(
    (t) =>
      [buyer, target].every((c) => {
        const plotData = c[t]?.breakdowns?.[SEGMENT_KEYS[tab]] || {};
        if (Object.keys(plotData).length === 0) {
          return false;
        }
        // filter data not null
        return Object.keys(plotData).some(
          (k) =>
            plotData[k].length > 0 &&
            plotData[k].filter((item) => item?.[1] && item[1] !== null).length >
              0,
        );
      }),
  );
  return timelinesBothHaveData[0] || condition.Timeline[0].value;
};

function TotalRevenue(props) {
  const {
    chartData = {},
    projectId,
    getTotalRevenueData,
    clearTotalRevenueData,
  } = props;
  const { tab: defaultValue, expand: initialExpand } = getValueFromSearchParam(
    searchParamKeys.tab,
    searchParamKeys.expand,
  );
  const { theme } = useTheme();
  const isLightMode = theme === 'light';
  const history = useHistory();
  const [isLoading, setIsLoading] = useState(true);
  const [allData, setAllData] = useState({});
  const [bySegmentOption, setBySegmentOption] = useState([]);
  const [byGeographyOption, setByGeographyOption] = useState([]);
  const [metrics, setMetrics] = useState();
  const [currencyISOCodes, setCurrencyISOCodes] = useState([]);
  const [expand, setExpand] = useState(initialExpand);
  const [timeline, setTimeline] = useState('LTMQ');
  const [isTabChanging, setIsTabChanging] = useState(0);
  const totalRevenueRef = [useRef(null), useRef(null)];

  // format后端数据到series里
  const buildSeriesDataBySegments = (_segment, allTicks) => {
    const series = [];
    let colorIndex = 0;
    const segment = mergeSegmentDataWithDifferentCase(_segment);
    const segmentsKeys = Object.keys(segment);

    // sort series by their total amount
    const sumValueByKey = (serie, obj) => {
      const keyValue = obj[serie];
      if (!keyValue || !Array.isArray(keyValue)) {
        return 0;
      }

      return keyValue.reduce((acc, e) => {
        if (!e || !e[1]) {
          return acc;
        }
        return acc + e[1];
      }, 0);
    };
    segmentsKeys.sort(
      (a, b) => sumValueByKey(b, segment) - sumValueByKey(a, segment),
    );

    segmentsKeys.forEach((k) => {
      const seriesData = [];
      // 每个series data的数组，格式为:{name,y}
      segment[k].forEach((j, i) => {
        if (!j) {
          seriesData.push({
            name: allTicks[i],
            y: null,
            currencyIsoCode: 'USD',
          });
        } else {
          seriesData.push({
            name: j[0].tick,
            y: j[1],
            currencyIsoCode: j[0].currencyIsoCode,
          });
        }
      });

      // remove series without valid data
      if (Array.isArray(seriesData) && seriesData.some((d) => d.y !== null)) {
        series.push({
          name: titleCase(String(k).toLowerCase()),
          type: 'column',
          data: seriesData,
          color: NewStackColors[colorIndex],
          borderWidth: 0,
        });
      }

      colorIndex++;
    });

    return series;
  };

  // 首次初始化
  const formatAndSetStackbarSeries = (data, year) => {
    const _segmentOption = [],
      _geographyOption = [],
      _currenctISOCode = [],
      _metrics = [];
    // d为buyer和target
    for (const d in data) {
      // bySegment和byGeography
      SEGMENT_KEYS.forEach((current) => {
        let tempChartOption;

        if (data[d].isManual) {
          tempChartOption = { isManual: true };
        } else if (
          data[d].Y5?.breakdowns[current]['Total Revenue']?.filter((obj) => obj)
            .length === 0
        ) {
          tempChartOption = { isManual: true };
        } else {
          if (isLightMode) {
            defaultTotalRevenueChartOption.yAxis[0].gridLineColor = '#000000';
            delete defaultTotalRevenueChartOption.yAxis[0]?.labels?.style
              ?.color;
            delete defaultTotalRevenueChartOption.yAxis[0]?.stackLabels?.style
              ?.color;
            // defaultTotalRevenueChartOption.yAxis[1].gridLineColor = "#000000";
            delete defaultTotalRevenueChartOption.yAxis[1]?.labels?.style
              ?.color;
            delete defaultTotalRevenueChartOption.yAxis[1]?.stackLabels?.style
              ?.color;
            delete defaultTotalRevenueChartOption.xAxis?.gridLineColor;
            delete defaultTotalRevenueChartOption.xAxis?.lineColor;
            delete defaultTotalRevenueChartOption.xAxis?.tickColor;
            delete defaultTotalRevenueChartOption.xAxis?.labels?.style?.color;
          } else {
            defaultTotalRevenueChartOption.tooltip.style = {
              color: '#dbdbdb',
            };
          }
          tempChartOption = produce(defaultTotalRevenueChartOption, (draft) => {
            // x轴刻度赋值
            draft.xAxis.categories = data[d][year].ticks.main;
            draft.xAxis.title.text = data[d][year].ticks.secondary || null;

            //添加stacklabel

            if (YEARS_NEED_STACKLABEL.includes(year)) {
              draft.yAxis[0].stackLabels.enabled = true;
              draft.yAxis[0].stackLabels.formatter = function () {
                const that = this;

                return D_Currency.format({
                  value: that.total,
                  currencyIsoCode: data[d].currencyIsoCode.revenue,
                });
              };
            }

            // 循环sereis
            draft.series = buildSeriesDataBySegments(
              data[d][year].breakdowns[current],
              data[d][year].ticks.main,
            );
            // 设置标题
            draft.title.text = data[d].companyName || d;
            draft.currencyIsoCode = data[d].currencyIsoCode;

            // set layout as non-columns to ensure the display of the whole legend titles
            draft.legend = {
              alignColumns: false,
            };

            // set tickamount to align the gridlines
            // and show item with greater value in the bottom of the stack
            draft.yAxis.forEach((y) => {
              y.tickAmount = 5;
              y.reversedStacks = false;
            });
          });
        }
        current === 'bySegment' && _segmentOption.push(tempChartOption);
        current === 'byGeography' && _geographyOption.push(tempChartOption);
      });
      // _currenctISOCode.push(data[d].currencyISOCode);
      _metrics.push(data[d][year]?.metrics);
    }
    // const yAxisSyncedOption = getMaxYAxisNum(_segmentOption);
    setBySegmentOption(_segmentOption);
    setByGeographyOption(_geographyOption);
    setCurrencyISOCodes(_currenctISOCode);
    setMetrics(_metrics);
    setIsLoading(false);
  };
  // fetch data
  const fetchTotalRevenue = async (year = 'LTMQ') => {
    const { buyer = {}, target = {} } = chartData;

    const hasBuyerData = Object.keys(buyer).length > 0;
    const hasTargetData = Object.keys(target).length > 0;
    if (hasBuyerData && hasTargetData) {
      const defaultTimeline = getFirstTimelineBothHaveData(
        chartData,
        defaultValue || 0,
      );

      setAllData(chartData);
      setTimeline(defaultTimeline);
      formatAndSetStackbarSeries(chartData, defaultTimeline);
    }
    // else {
    //   await getTotalRevenueData(projectId);
    // }
  };
  const handleSelectYear = (year) => {
    if (year !== timeline) {
      totalRevenueRef[0].current.drawLine('default');
      totalRevenueRef[1].current.drawLine('default');
      setTimeline(year);
      formatAndSetStackbarSeries(allData, year);
    }
  };
  const expandCharts = () => {
    setExpand(!expand);
    if (!expand) {
      setURLSearchParam(history, {
        [searchParamKeys.expand]: true,
      });
    } else {
      removeValueFromSearchParam(history, [searchParamKeys.expand]);
    }
  };

  const onTabChange = (e, tab) => {
    setIsTabChanging(!isTabChanging);
    setTimeline(timeline);
    if (tab) {
      setURLSearchParam(history, { tab });

      const defaultTimeline = getFirstTimelineBothHaveData(chartData, tab);
      if (defaultTimeline !== timeline) {
        handleSelectYear(defaultTimeline);
      }
    }
  };

  useEffect(() => {
    fetchTotalRevenue();
  }, [JSON.stringify(chartData)]);

  useEffect(() => {
    getTotalRevenueData(projectId);
    return () => {
      clearTotalRevenueData(projectId);
    };
  }, [projectId]);

  return (
    <div
      key={JSON.stringify(chartData)}
      className={`analysis-right-wrapper ${expand && 'full-screen'}`}
    >
      <div className="title no-border expand-box">
        <div>Total Revenue</div>
        {!isLoading && (
          <ExpandButton
            initialExpand={initialExpand}
            onClick={expandCharts}
            scrollPageFooter
          />
        )}
      </div>

      {isLoading ? (
        <Loading />
      ) : (
        <Tabs
          className="stock-tab-wrapper"
          defaultValue={defaultValue}
          onChange={onTabChange}
          tabs={[
            {
              label: 'By Segment',
              children: (
                <TotalRevenueChart
                  tabKey="bySegment"
                  handleSelectYear={handleSelectYear}
                  data={bySegmentOption}
                  metrics={metrics}
                  expand={expand}
                  timeline={timeline}
                  isTabChanging={isTabChanging}
                  currencyIsoCode={currencyISOCodes[0]}
                  ref={totalRevenueRef[0]}
                  names={{
                    buyerName: chartData.buyer?.companyName,
                    targetName: chartData.target?.companyName,
                  }}
                />
              ),
            },
            {
              label: 'By Geography',
              children: (
                <TotalRevenueChart
                  tabKey="byGeography"
                  handleSelectYear={handleSelectYear}
                  data={byGeographyOption}
                  metrics={metrics}
                  expand={expand}
                  timeline={timeline}
                  isTabChanging={isTabChanging}
                  currencyIsoCode={currencyISOCodes[1]}
                  ref={totalRevenueRef[1]}
                  names={{
                    buyerName: chartData.buyer?.companyName,
                    targetName: chartData.target?.companyName,
                  }}
                />
              ),
            },
          ]}
        />
      )}
    </div>
  );
}
const mapStateToProps = ({ common, analysis }) => {
  const projectId = common.currentId;
  return {
    chartData: (analysis.totalRevenueData || {})[projectId],
    projectId,
  };
};

const mapDispatchToProps = (dispatch) => ({
  async getTotalRevenueData(id) {
    await dispatch(action.fetchTotalRevenue(id, true));
  },
  clearTotalRevenueData(id) {
    dispatch(action.changeTotalRevenueData({ id, data: {} }));
  },
});
export default connect(mapStateToProps, mapDispatchToProps)(memo(TotalRevenue));
